/**
 * shell/SettingsDialog.tsx —— **设置面板壳**（U-S1 新件：居中对话框＋遮罩＋Esc/遮罩关闭＋焦点归还）。
 *
 * 真源（102，逐字）：
 *  · `views/AppShellHost.vue:1528` —— 面板本体（`SettingsPanel`）落 **`shell.overlay`**
 *    （§3.0 铁律 3「真正『浮』的只有 shell.overlay」＋§3.7 层容器 pointer-events:none、条目自行 opt-in）。
 *  · `SettingsPanel.vue:418-452` —— 居中形态：层容器 fixed inset 0＋面板 `align-self:center`＋
 *    `translateX((sidebar_w − rightbar_w)/2)`＝**按主区居中**（列宽单源＝`shell/columns.ts` 求解值，
 *    本壳零新增计算——旧树注释逐字「此处零新增计算」）。
 *  · `AppShellHost.vue:1541-1552` —— 遮罩＝纯点击面（`aria-hidden`，点击即关闭；U-12 剩余⑥
 *    「点击空白处关闭」）；面板 DOM 序在遮罩之后 ⇒ 自然叠上。
 *  · `SettingsPanel.vue:290-295` —— 焦点契约：`useDialogA11y`（模态陷阱＋Esc＋**焦点归还**）。
 *    新树等价＝`shell/useOverlayFocus`（开⇒记打开者并移焦入层；关⇒归还；开期间 Esc⇒onClose）——
 *    本壳不自造第二套焦点机制（宿主 AppShell 持 useOverlayFocus 并把 containerRef 交本壳）。
 *
 * 形态（硬口径1/2）：**不借用右栏/sheet 类**（现树缺陷根因＝`xg-rightbar` 被网格 0 宽列压成
 * 32px 跑出视口）；桌面＝居中对话框；移动档同一对话框自动收窄到 `100vw−48px`（CSS 承担，见
 * `styles/shell.css` U-S1 段）。
 */
import React from "react";

export interface SettingsDialogProps {
  /** 面板本体（`SettingsPanel` 实例；本壳不解释其语义） */
  children: React.ReactNode;
  /** 关闭（✕／遮罩点击／Esc——宿主 `closeSettings` 单点） */
  onClose: () => void;
  /** 按主区居中的列宽事实（px；单源＝`shell/columns.ts::computeColumns`，本壳零新增计算） */
  sidebarWidthPx: number;
  rightbarWidthPx: number;
  /** 焦点容器锚（宿主 `useOverlayFocus.open` 传入；开⇒移焦入层，关⇒归还打开者）
   *  型＝`MutableRefObject<HTMLElement | null>`（与 `RightBar` 的 containerRef 注入面同型） */
  containerRef?: React.MutableRefObject<HTMLElement | null>;
}

/** 主区居平移（旧树 :449 逐式：`translateX((sidebar_w − rightbar_w)/2)`——
 *  层容器全屏居中＝视口中心；主区中心＝视口中心 + (sidebar − rightbar)/2） */
export function settingsDialogShift(sidebarWidthPx: number, rightbarWidthPx: number): string {
  return `translateX(${(sidebarWidthPx - rightbarWidthPx) / 2}px)`;
}

/** 对话框壳：`fixed inset 0` 层＋纯点击遮罩＋按主区居中的面板舞台 */
export function SettingsDialog(props: SettingsDialogProps): React.ReactElement {
  return (
    <div className="xg-settings-dialog" data-testid="settings-dialog">
      {/* 遮罩＝纯点击面（aria-hidden；点击即关闭——旧树 xg-host-scrim 逐语义） */}
      <div
        className="xg-settings-scrim"
        data-testid="settings-scrim"
        aria-hidden="true"
        onClick={props.onClose}
      />
      {/* 舞台＝居中载体：`justify-content:center`（视口居中）＋ 平移（主区居中） */}
      <div
        className="xg-settings-dialog__stage"
        data-testid="settings-surface"
        role="dialog"
        aria-modal="true"
        ref={props.containerRef as React.RefObject<HTMLDivElement>}
        style={{ transform: settingsDialogShift(props.sidebarWidthPx, props.rightbarWidthPx) }}
      >
        {props.children}
      </div>
    </div>
  );
}

export default SettingsDialog;
